import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

/**
 * After the current route's data has had a moment to land, quietly warm the
 * other tabs in the background so a subsequent click feels instant.
 *
 * Strategy:
 *   - Briefly wait for the active page to paint, then fire every other
 *     tab's query in parallel. Vercel's edge CDN caches the responses
 *     (see Cache-Control headers on the API handlers), so parallel
 *     requests don't hammer the origin after the first visitor.
 *   - Lean on react-query's staleTime (set in main.tsx) so the prefetched
 *     data gets reused when the user actually navigates to that tab.
 *   - Skip prefetching a tab if we're currently *on* that tab.
 */

type PrefetchJob = {
  key: readonly unknown[];
  url: string;
  matchPath: string;
};

// The singles + top-pulls endpoints are the slowest, so we prefetch them
// first after the much-cheaper packs/collections calls.
const JOBS: readonly PrefetchJob[] = [
  { key: ["top-pulls"], url: "/api/top-pulls?perPack=8&limit=60", matchPath: "/" },
  { key: ["packs"], url: "/api/packs", matchPath: "/ev-packs" },
  {
    key: ["collections", "7d"],
    url: "/api/trending-collections?window=7d",
    matchPath: "/collections",
  },
  {
    key: ["singles", "7d"],
    url: "/api/trending-singles?window=7d",
    matchPath: "/singles",
  },
] as const;

type IdleHandle = number;
type IdleCallback = (deadline: { didTimeout: boolean }) => void;

function runAtIdle(cb: IdleCallback, timeout = 1500): IdleHandle {
  const win = window as Window & {
    requestIdleCallback?: (cb: IdleCallback, opts?: { timeout: number }) => number;
  };
  if (typeof win.requestIdleCallback === "function") {
    return win.requestIdleCallback(cb, { timeout });
  }
  return window.setTimeout(() => cb({ didTimeout: true }), timeout);
}

function cancelIdle(handle: IdleHandle) {
  const win = window as Window & {
    cancelIdleCallback?: (h: number) => void;
  };
  if (typeof win.cancelIdleCallback === "function") {
    win.cancelIdleCallback(handle);
  } else {
    window.clearTimeout(handle);
  }
}

export function BackgroundPrefetcher() {
  const qc = useQueryClient();
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    const idleHandles: IdleHandle[] = [];

    // Wait one idle tick for the active page to paint, then fan out all
    // the warmups in parallel. Edge-CDN caching means subsequent visitors
    // pay virtually nothing for each prefetch.
    const handle = runAtIdle(() => {
      if (cancelled) return;
      for (const job of JOBS) {
        if (job.matchPath === pathname) continue;
        void qc.prefetchQuery({
          queryKey: job.key,
          queryFn: async () => (await fetch(job.url)).json(),
        });
      }
    }, 800);
    idleHandles.push(handle);

    return () => {
      cancelled = true;
      idleHandles.forEach(cancelIdle);
    };
  }, [qc, pathname]);

  return null;
}
